import { Fragment, isValidElement } from 'react';
import {
  Breadcrumbs,
  Tabs,
  Typography,
  TypographyVariants,
  TypographyColors,
  Badge,
  BadgeSizes,
} from '@carboledger/ui';
import { cx } from '@/lib/utils';
import { DiscrepanciesCell } from '../tableCells';
import styles from './DetailPageHeader.module.scss';

// A meta entry renders as text, a status badge or a clickable discrepancy badge, per `type`.
const MetaFieldValue = ({ field }) => {
  if (field.type === 'status') {
    return (
      <Badge variant={field.tone} size={BadgeSizes.sm} withDot>
        {field.value}
      </Badge>
    );
  }
  // The shared cell owns the rule: a zero count is a plain badge, never a button.
  if (field.type === 'discrepancy') {
    return <DiscrepanciesCell count={field.count} onOpen={field.onClick} />;
  }
  if (isValidElement(field.value)) return field.value;
  return (
    <Typography variant={TypographyVariants.bodySm} color={TypographyColors.primary}>
      {field.value}
    </Typography>
  );
};

// A flat list is the one row it has always been; an array of arrays is a row apiece.
const toMetaRows = (metaFields) => (Array.isArray(metaFields[0]) ? metaFields : [metaFields]);

/**
 * Props:
 *   fields  the row's meta fields, separated in order
 */
const MetaRow = ({ fields }) => (
  <div className={styles.metaRow}>
    {fields.map((field, index) => (
      <Fragment key={field.id}>
        {index > 0 && <span className={styles.metaSep} />}
        <div
          className={styles.metaField}
          // A fixed width is what lines the row below up under the one above.
          style={field.width ? { width: field.width } : undefined}
        >
          <Typography variant={TypographyVariants.caption} color={TypographyColors.placeholder}>
            {field.label}
          </Typography>
          <MetaFieldValue field={field} />
        </div>
      </Fragment>
    ))}
  </div>
);

/**
 * Props:
 *   breadcrumbItems  Breadcrumbs' own items shape
 *   title            the entity's business id, shown as the page title
 *   actions          node — the button row next to the title (page-specific)
 *   metaFields       Array<Field | Array<Field>> where Field is { id, label, value, type?, tone?,
 *                    ariaLabel?, onClick?, width? } — an array entry is its own row, as
 *                    FieldSection's fields pair; `value` may be a node (e.g. a link Button)
 *                    instead of text; `width` pins a column so the row below lines up under it
 *   tabs             Tabs' own tabs shape
 *   activeTab        current tab value
 *   onTabChange      (value) => void
 *   tabsAriaLabel    Tabs' aria-label
 *   compactMeta      opts the meta bar into the current design's tighter metrics
 */
export const DetailPageHeader = ({
  breadcrumbItems,
  title,
  actions,
  metaFields,
  tabs,
  activeTab,
  onTabChange,
  tabsAriaLabel,
  compactMeta = false,
}) => (
  <div className={styles.headerCard}>
    <div className={styles.breadcrumbRow}>
      <Breadcrumbs items={breadcrumbItems} />
    </div>

    <div className={styles.mainRow}>
      <div className={styles.titleRow}>
        <Typography
          variant={TypographyVariants.h5}
          color={TypographyColors.primary}
          className={styles.title}
        >
          {title}
        </Typography>
        {actions}
      </div>

      <div className={cx(styles.metaBar, compactMeta && styles.metaBarCompact)}>
        {toMetaRows(metaFields).map((row) => (
          <MetaRow key={row[0]?.id} fields={row} />
        ))}
      </div>
    </div>

    <div className={styles.tabsRow}>
      <Tabs tabs={tabs} value={activeTab} onChange={onTabChange} aria-label={tabsAriaLabel} />
    </div>
  </div>
);
